import { execFile } from 'child_process'
import { existsSync } from 'fs'
import { promisify } from 'util'
import { logger } from '../logger'
import { getActiveProfileDir } from './hermes-profile'
import { spawnHermesWithBin } from './hermes-process'

interface SupervisedGateway {
  pid: number
  child: ReturnType<typeof spawnHermesWithBin>
  hermesBin: string
  profileDir: string
  startedAt: number
}

/**
 * Per-profile state for our supervised gateway.
 *
 * - `current` is the most recent child we spawned for this profile. When it
 *   exits, the exit handler clears this slot.
 * - `respawnTimer` is a pending respawn that was scheduled because the
 *   previous child died and no replacement has been started yet. The next
 *   call to `startGatewayRunManaged` for the same profile clears it: a fresh
 *   start is already happening, so a respawn would race with it.
 *
 * This is what makes `/restart` safe. The flow is:
 *   1. `/restart` calls `hermes gateway stop` (CLI) which kills our child.
 *   2. That child's exit handler schedules a respawn timer (step T+0).
 *   3. `/restart` then calls `startGatewayRunManaged` for the same profile.
 *   4. That call clears the pending respawn timer (we're starting a new one
 *      anyway) and registers the fresh child.
 *
 * Net result: exactly one new gateway per `/restart`, no orphans.
 */
interface ProfileState {
  current: SupervisedGateway | null
  respawnTimer: NodeJS.Timeout | null
  respawnAttempts: number
  /**
   * Set when this profile's gateway is being retired (profile delete /
   * managed shutdown). While set, the exit handler and any already-queued
   * respawn timer must not re-create the gateway. This is a memory-level
   * guard: the on-disk gateway_state.json is wiped with the profile
   * directory and respawn never read it anyway.
   */
  suppressRespawn: boolean
}

const profileState = new Map<string, ProfileState>()

/** Delay before respawning a gateway that exited unexpectedly. */
const RESPAWN_DELAY_MS = 2000
const RESPAWN_STABLE_RUN_MS = 30000
const MAX_RESPAWN_ATTEMPTS = 3
const DEFAULT_SHUTDOWN_TIMEOUT_MS = 2000
const execFileAsync = promisify(execFile)

interface GatewayExitDisposition {
  respawn: boolean
  reason: string
}

export interface ManagedGatewayShutdownResult {
  signaled: number
  forced: number
  errors: number
}

function getOrCreateProfileState(profileDir: string): ProfileState {
  let state = profileState.get(profileDir)
  if (!state) {
    state = { current: null, respawnTimer: null, respawnAttempts: 0, suppressRespawn: false }
    profileState.set(profileDir, state)
  }
  return state
}

type KillWindowsProcessTree = (pid: number) => Promise<void>

function classifyManagedGatewayExit(
  code: number | null,
  signal: NodeJS.Signals | null,
): GatewayExitDisposition {
  if (code === 0) return { respawn: false, reason: 'exit-code-0' }
  if (signal === 'SIGTERM' || signal === 'SIGINT') {
    return { respawn: false, reason: `signal-${signal.toLowerCase()}` }
  }
  return { respawn: true, reason: 'unexpected-exit' }
}

function clearRespawnTimer(state: ProfileState, profileDir: string): void {
  if (!state.respawnTimer) return
  clearTimeout(state.respawnTimer)
  state.respawnTimer = null
  logger.info('[gateway-runner] cancelled pending respawn profileDir=%s', profileDir)
}

async function taskkillWindowsProcessTree(pid: number): Promise<void> {
  await execFileAsync('taskkill.exe', ['/PID', String(pid), '/T', '/F'], {
    timeout: 5000,
    windowsHide: true,
  })
}

async function stopManagedGateway(
  entry: SupervisedGateway,
  opts: {
    timeoutMs: number
    platform: NodeJS.Platform
    killWindowsProcessTree: KillWindowsProcessTree
  },
): Promise<{ forced: boolean; error?: unknown }> {
  if (opts.platform === 'win32') {
    try {
      await opts.killWindowsProcessTree(entry.pid)
      return { forced: true }
    } catch (err) {
      logger.warn(err, '[gateway-runner] taskkill failed for managed gateway pid=%s; falling back to child.kill', entry.pid)
      try {
        entry.child.kill('SIGKILL')
        return { forced: true, error: err }
      } catch (killErr) {
        return { forced: true, error: killErr }
      }
    }
  }

  return new Promise(resolve => {
    let settled = false
    let forced = false

    const finish = (error?: unknown) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      entry.child.off('exit', onExit)
      resolve({ forced, error })
    }

    const onExit = () => finish()
    const timer = setTimeout(() => {
      forced = true
      try {
        entry.child.kill('SIGKILL')
      } catch (err) {
        finish(err)
        return
      }
      finish()
    }, opts.timeoutMs)

    entry.child.once('exit', onExit)

    try {
      const signaled = entry.child.kill('SIGTERM')
      if (!signaled) finish(new Error(`Failed to signal managed gateway pid=${entry.pid}`))
    } catch (err) {
      finish(err)
    }
  })
}

export async function shutdownManagedGateways(
  opts: {
    timeoutMs?: number
    platform?: NodeJS.Platform
    killWindowsProcessTree?: KillWindowsProcessTree
  } = {},
): Promise<ManagedGatewayShutdownResult> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_SHUTDOWN_TIMEOUT_MS
  const platform = opts.platform ?? process.platform
  const killWindowsProcessTree = opts.killWindowsProcessTree ?? taskkillWindowsProcessTree
  const stops: Promise<{ forced: boolean; error?: unknown }>[] = []
  let signaled = 0

  for (const [profileDir, state] of profileState) {
    // Shutdown is an intentional stop: no exit handler / respawn timer may
    // re-create these gateways.
    state.suppressRespawn = true
    clearRespawnTimer(state, profileDir)

    const entry = state.current
    if (!entry) {
      profileState.delete(profileDir)
      continue
    }

    state.current = null
    signaled += 1
    logger.info('[gateway-runner] stopping managed gateway profileDir=%s pid=%s', profileDir, entry.pid)
    stops.push(stopManagedGateway(entry, { timeoutMs, platform, killWindowsProcessTree }))
    profileState.delete(profileDir)
  }

  const results = await Promise.all(stops)
  const forced = results.filter(result => result.forced).length
  const errors = results.filter(result => result.error).length

  if (signaled > 0) {
    logger.info('[gateway-runner] managed gateway shutdown complete signaled=%s forced=%s errors=%s', signaled, forced, errors)
  }

  return { signaled, forced, errors }
}

export async function retireManagedGatewayForProfile(
  profileDir: string,
  opts: {
    timeoutMs?: number
    platform?: NodeJS.Platform
    killWindowsProcessTree?: KillWindowsProcessTree
  } = {},
): Promise<ManagedGatewayShutdownResult> {
  const state = profileState.get(profileDir)
  if (!state) return { signaled: 0, forced: 0, errors: 0 }

  // Retiring is an intentional stop (profile delete / migration): suppress
  // any respawn so a stale exit handler or pending timer cannot re-create
  // the gateway inside a directory that is about to disappear.
  state.suppressRespawn = true
  clearRespawnTimer(state, profileDir)

  const entry = state.current
  state.current = null
  profileState.delete(profileDir)

  if (!entry) return { signaled: 0, forced: 0, errors: 0 }

  const timeoutMs = opts.timeoutMs ?? DEFAULT_SHUTDOWN_TIMEOUT_MS
  const platform = opts.platform ?? process.platform
  const killWindowsProcessTree = opts.killWindowsProcessTree ?? taskkillWindowsProcessTree

  logger.info('[gateway-runner] retiring managed gateway profileDir=%s pid=%s', profileDir, entry.pid)
  const result = await stopManagedGateway(entry, { timeoutMs, platform, killWindowsProcessTree })
  const forced = result.forced ? 1 : 0
  const errors = result.error ? 1 : 0

  logger.info(
    '[gateway-runner] managed gateway retire complete profileDir=%s signaled=1 forced=%s errors=%s',
    profileDir,
    forced,
    errors,
  )

  return { signaled: 1, forced, errors }
}

/**
 * Returns the PIDs of every currently supervised managed gateway. Used by
 * the cross-platform reaper (ADR-0011) to distinguish "we own it" from
 * "an external process holds the lock" — we never kill external PIDs.
 */
export function getAllManagedGatewayPids(): number[] {
  const pids: number[] = []
  for (const state of profileState.values()) {
    const pid = state.current?.pid
    if (typeof pid === 'number' && pid > 0) pids.push(pid)
  }
  return pids
}

export function startGatewayRunManaged(
  hermesBin: string,
  opts: {
    profileDir?: string
    /**
     * Test seam: override `process.platform` so unit tests can drive the
     * non-Windows SIGTERM path without spawning a real taskkill.
     * Not used in production — defaults to `process.platform`.
     */
    platformOverride?: NodeJS.Platform
    /**
     * Test seam: override the Windows process-tree killer.
     */
    killWindowsProcessTreeOverride?: KillWindowsProcessTree
  } = {},
): { pid: number | null; reused: boolean } {
  return startGatewayRunManagedInternal(hermesBin, {
    profileDir: opts.profileDir,
    preserveRespawnAttempts: false,
    platformOverride: opts.platformOverride,
    killWindowsProcessTreeOverride: opts.killWindowsProcessTreeOverride,
  })
}

function startGatewayRunManagedInternal(
  hermesBin: string,
  opts: {
    profileDir?: string
    preserveRespawnAttempts?: boolean
    platformOverride?: NodeJS.Platform
    killWindowsProcessTreeOverride?: KillWindowsProcessTree
  } = {},
): { pid: number | null; reused: boolean } {
  const profileDir = opts.profileDir || getActiveProfileDir()
  const state = getOrCreateProfileState(profileDir)

  // A fresh start for this profile is an explicit user/system intent to run
  // the gateway, so any prior retirement suppression no longer applies.
  state.suppressRespawn = false

  // A new spawn for this profile cancels any pending respawn from a previous
  // unexpected exit. Without this, `/restart` (stop -> start) would race
  // against the respawn timer and end up with two gateways on the same port.
  clearRespawnTimer(state, profileDir)
  if (!opts.preserveRespawnAttempts) {
    state.respawnAttempts = 0
  }

  // ★ ADR-0011: stop the previous managed child before spawning a new one
  // for the same profile. Without this, the reference is silently replaced
  // and the old `gateway run --replace` process leaks as a ghost — the CLI's
  // `gateway stop` may not reach it (it spawns a detached Python child whose
  // PID isn't necessarily the one written to gateway.pid). Fire-and-forget
  // so a slow kill doesn't block the user's config update; the periodic
  // reaper (gateway-autostart) is the safety net.
  if (state.current) {
    const old = state.current
    state.current = null
    logger.info(
      '[gateway-runner] stopping previous managed gateway before respawn profileDir=%s oldPid=%s',
      profileDir, old.pid,
    )
    stopManagedGateway(old, {
      timeoutMs: 3000,
      platform: opts.platformOverride ?? process.platform,
      killWindowsProcessTree: opts.killWindowsProcessTreeOverride ?? taskkillWindowsProcessTree,
    }).catch(err => {
      logger.warn(
        err, '[gateway-runner] failed to stop previous managed gateway profileDir=%s oldPid=%s',
        profileDir, old.pid,
      )
    })
  }

  const child = spawnHermesWithBin(hermesBin, ['gateway', 'run', '--replace'], {
    // On Windows, detached:true uses DETACHED_PROCESS. That makes
    // CREATE_NO_WINDOW/windowsHide ineffective and exposes the venv
    // Scripts\python.exe console. The server already supervises this child,
    // so keep it attached there; POSIX still needs a detached process group.
    detached: process.platform !== 'win32',
    stdio: 'ignore',
    windowsHide: true,
    env: {
      ...process.env,
      HERMES_HOME: profileDir,
    },
  })
  child.unref()

  const pid = child.pid ?? null
  if (pid) {
    const entry: SupervisedGateway = { pid, child, hermesBin, profileDir, startedAt: Date.now() }
    state.current = entry

    child.on('exit', (code, signal) => {
      // Only act if this is still the active child for the profile. A new
      // start for the same profile replaces `state.current` and we don't
      // want the old child's exit to trigger anything.
      if (state.current?.pid !== pid) return
      state.current = null
      const disposition = classifyManagedGatewayExit(code, signal)
      if (!disposition.respawn) {
        logger.info(
          '[gateway-runner] managed gateway exited without respawn (profileDir=%s pid=%s code=%s signal=%s reason=%s)',
          profileDir, pid, code, signal, disposition.reason,
        )
        return
      }
      // The profile may have been retired (deleted / shut down) between the
      // child dying and this handler running. Never re-create a gateway for
      // a profile that is intentionally gone.
      if (state.suppressRespawn) {
        logger.info(
          '[gateway-runner] managed gateway exit suppressed by retirement (profileDir=%s pid=%s code=%s signal=%s)',
          profileDir, pid, code, signal,
        )
        return
      }
      if (Date.now() - entry.startedAt >= RESPAWN_STABLE_RUN_MS) {
        state.respawnAttempts = 0
      }
      state.respawnAttempts += 1

      if (state.respawnAttempts > MAX_RESPAWN_ATTEMPTS) {
        logger.error(
          '[gateway-runner] gateway exited unexpectedly and reached respawn limit (profileDir=%s pid=%s code=%s signal=%s attempts=%s maxAttempts=%s)',
          profileDir, pid, code, signal, state.respawnAttempts - 1, MAX_RESPAWN_ATTEMPTS,
        )
        return
      }

      logger.warn(
        '[gateway-runner] gateway exited unexpectedly (profileDir=%s pid=%s code=%s signal=%s attempt=%s/%s), respawning in %dms',
        profileDir, pid, code, signal, state.respawnAttempts, MAX_RESPAWN_ATTEMPTS, RESPAWN_DELAY_MS,
      )

      state.respawnTimer = setTimeout(() => {
        state.respawnTimer = null
        // Second guard: the profile may have been retired while the timer
        // was pending. The exit handler check above catches the common case,
        // but the timer itself must re-check because it may have been
        // scheduled before retirement and outlived the retire path.
        if (state.suppressRespawn) {
          logger.info(
            '[gateway-runner] pending respawn cancelled after profile retirement (profileDir=%s oldPid=%s)',
            profileDir, pid,
          )
          return
        }
        // Defense in depth: the profile directory may have been removed
        // through a path that never called retire (e.g. external CLI), so a
        // respawn would recreate a ghost gateway under a dead directory.
        if (!existsSync(profileDir)) {
          logger.warn(
            '[gateway-runner] skipping respawn because profile directory no longer exists (profileDir=%s oldPid=%s)',
            profileDir, pid,
          )
          return
        }
        try {
          const next = startGatewayRunManagedInternal(hermesBin, {
            profileDir,
            preserveRespawnAttempts: true,
          })
          logger.info(
            '[gateway-runner] gateway respawned (oldPid=%s newPid=%s profileDir=%s attempt=%s/%s)',
            pid, next.pid, profileDir, state.respawnAttempts, MAX_RESPAWN_ATTEMPTS,
          )
        } catch (err) {
          logger.error(err, '[gateway-runner] failed to respawn gateway after unexpected exit')
        }
      }, RESPAWN_DELAY_MS)
      // Don't keep the event loop alive just for a pending respawn.
      state.respawnTimer.unref()
    })
  }

  return { pid, reused: false }
}
